import React from "react";
import { makeStyles } from "@mui/styles";
import Dialog from "@mui/material/Dialog";
import { DIALOG_BUTTON_BORDER_RADIUS, DIALOG_BUTTON_MIN_HEIGHT } from "./constants";
import DialogContent from "@mui/material/DialogContent";
import Slide from "@mui/material/Slide";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles({
  paper: {
    width: "90%",
    maxWidth: "380px",
    fontFamily: "objektiv-mk2, sans-serif",
    borderRadius: "4px",
    overflow: "hidden",
  },
  content: {
    padding: "0 !important",
  },
  header: {
    padding: "16px 20px",
    color: "white",
    textAlign: "center",
    fontSize: "18px",
    fontWeight: "bold",
    fontFamily: "objektiv-mk2, sans-serif",
  },
  headerRed: {
    backgroundColor: "#c62828",
  },
  headerBlue: {
    backgroundColor: "#4A7396",
  },
  body: {
    padding: "20px",
    fontSize: "14px",
    lineHeight: "1.4",
    color: "#1f2937",
    fontFamily: "objektiv-mk2, sans-serif",
    whiteSpace: "pre-line",
  },
  footer: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "12px",
    padding: "16px 20px",
    borderTop: "1px solid #e5e7eb",
    backgroundColor: "#f9fafb",
  },
  footerSplit: {
    justifyContent: "space-between",
  },
  button: {
    fontFamily: "objektiv-mk2, sans-serif",
    fontSize: "12px",
    padding: "6px 14px",
    height: DIALOG_BUTTON_MIN_HEIGHT,
    minHeight: DIALOG_BUTTON_MIN_HEIGHT,
    boxSizing: "border-box",
    borderRadius: DIALOG_BUTTON_BORDER_RADIUS,
    border: "1px solid #d1d5db",
    backgroundColor: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    "&:hover": {
      backgroundColor: "#f3f4f6",
    },
  },
  buttonPrimary: {
    backgroundColor: "#4A7396",
    color: "white",
    borderColor: "#3a5a7a",
    "&:hover": {
      backgroundColor: "#3a5a7a",
    },
  },
  buttonSmall: {
    fontSize: "12px",
    padding: "6px 14px",
    height: DIALOG_BUTTON_MIN_HEIGHT,
    minHeight: DIALOG_BUTTON_MIN_HEIGHT,
    boxSizing: "border-box",
  },
});

/**
 * ErrorDialog - Reusable dialog for errors and warnings.
 * @param {boolean} open
 * @param {function} onClose
 * @param {string} headerText - Header title (e.g. "Please note", "PLEASE NOTE:")
 * @param {string} contentText - Body text (can use \n for line breaks); ignored if content is provided
 * @param {React.ReactNode} [content] - Optional React node for body (e.g. JSX with <strong>); overrides contentText
 * @param {"red"|"blue"} [headerVariant="red"] - Header background color
 * @param {boolean} [footerSplit=false] - If true, footer uses space-between (e.g. Cancel left, YES Proceed right)
 * @param {Array<{label: string, onClick: function}>} [buttons] - Custom footer buttons; if omitted, single OK button that calls onClose
 * @param {"left"|"center"|"right"} [bodyAlign="left"] - Body text alignment
 * @param {string|number} [paperMaxWidth] - Optional paper max-width override (e.g. 560 or "560px")
 * @param {Object} [dialogProps] - Props to pass to MUI Dialog
 */
function ErrorDialog({
  open,
  onClose,
  headerText = "Please note",
  contentText = "",
  content,
  headerVariant = "red",
  footerSplit = false,
  buttons,
  bodyAlign = "left",
  paperMaxWidth,
  dialogProps,
}) {
  const classes = useStyles();
  const headerClass =
    headerVariant === "blue" ? classes.headerBlue : classes.headerRed;
  const useSmallButtons = Boolean(buttons && buttons.length > 0);

  const defaultButtons = [
    {
      label: "OK",
      onClick: onClose,
      primary: true,
    },
  ];
  const footerButtons = buttons && buttons.length > 0 ? buttons : defaultButtons;
  // Only buttons with primary: true get filled style; Cancel (no primary) gets transparent/outlined
  const isPrimary = (btn) => btn.primary === true;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      keepMounted
      classes={{ paper: classes.paper }}
      PaperProps={{
        elevation: 8,
        style: paperMaxWidth ? { maxWidth: paperMaxWidth } : undefined,
      }}
      {...dialogProps}
    >
      <div className={`${classes.header} ${headerClass}`}>{headerText}</div>
      <DialogContent className={classes.content}>
        <div className={classes.body} style={{ textAlign: bodyAlign }}>
          {content != null ? content : contentText}
        </div>
      </DialogContent>
      <div
        className={`${classes.footer} ${footerSplit ? classes.footerSplit : ""}`}
      >
        {footerButtons.map((btn, i) => (
          <button
            key={i}
            type="button"
            onClick={btn.onClick}
            className={`${classes.button} ${useSmallButtons ? classes.buttonSmall : ""} ${isPrimary(btn) ? classes.buttonPrimary : ""}`}
          >
            {btn.label}
          </button>
        ))}
      </div>
    </Dialog>
  );
}

export default ErrorDialog;
